'use client';

import { X } from 'lucide-react';
import React, { useState } from 'react';

import { DadosArquivamento } from '@/types';

interface MotivoArquivamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dados: DadosArquivamento) => void;
  cumprimentoId: string;
  isDesarquivamento?: boolean;
}

const MotivoArquivamentoModal: React.FC<MotivoArquivamentoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  cumprimentoId,
  isDesarquivamento = false,
}) => {
  const [motivo, setMotivo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const motivosArquivamento = [
    'Cumprimento concluído',
    'Processo extinto',
    'Acordo homologado',
    'Pagamento integral realizado',
    'Desistência da parte',
    'Prescrição',
    'Outros',
  ];

  const motivosDesarquivamento = [
    'Retomada do processo',
    'Nova fase de cumprimento',
    'Recurso interposto',
    'Decisão judicial',
    'Solicitação da parte',
    'Erro no arquivamento',
    'Outros',
  ];

  const motivosDisponiveis = isDesarquivamento ? motivosDesarquivamento : motivosArquivamento;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!motivo.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dadosArquivamento: DadosArquivamento = {
        data_arquivamento: new Date().toISOString(),
        motivo: motivo,
        observacoes: observacoes.trim() || undefined,
        usuario_arquivamento: 'Usuário Atual',
      };

      await onConfirm(dadosArquivamento);
      handleClose();
    } catch (error) {
      console.error('Erro ao processar arquivamento:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMotivo('');
    setObservacoes('');
    setIsSubmitting(false);
    onClose();
  };

  const handleMotivoChange = (novoMotivo: string) => {
    setMotivo(novoMotivo);
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="mx-4 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {isDesarquivamento ? 'Desarquivar Cumprimento' : 'Arquivar Cumprimento'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Motivo */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Motivo do {isDesarquivamento ? 'desarquivamento' : 'arquivamento'} *
              </label>
              <div className="space-y-2">
                {motivosDisponiveis.map((motivoOpcao) => (
                  <label key={motivoOpcao} className="flex items-center">
                    <input
                      type="radio"
                      name="motivo"
                      value={motivoOpcao}
                      checked={motivo === motivoOpcao}
                      onChange={(e) => handleMotivoChange(e.target.value)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2 text-sm text-gray-700">{motivoOpcao}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Campo de texto para motivo personalizado */}
            {motivo === 'Outros' && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Especifique o motivo
                </label>
                <input
                  type="text"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Digite o motivo específico..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  disabled={isSubmitting}
                  required
                />
              </div>
            )}

            {/* Observações adicionais */}
            {motivo !== 'Outros' && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Observações adicionais (opcional)
                </label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Digite observações adicionais se necessário..."
                  rows={3}
                  className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* Informação sobre o cumprimento */}
            <div className="rounded-md bg-gray-50 p-3">
              <p className="text-sm text-gray-600">
                <strong>Cumprimento:</strong> {cumprimentoId}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {isDesarquivamento
                  ? 'Este cumprimento será reativado e voltará para a lista principal.'
                  : 'Este cumprimento será movido para a seção de arquivados.'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                isDesarquivamento
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
              } disabled:cursor-not-allowed disabled:opacity-50`}
              disabled={
                !motivo.trim() || isSubmitting || (motivo === 'Outros' && !observacoes.trim())
              }
            >
              {isSubmitting
                ? isDesarquivamento
                  ? 'Desarquivando...'
                  : 'Arquivando...'
                : isDesarquivamento
                  ? 'Desarquivar'
                  : 'Arquivar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

MotivoArquivamentoModal;
export { MotivoArquivamentoModal };

export const useMotivoArquivamentoModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cumprimentoId, setCumprimentoId] = useState<string>('');
  const [isDesarquivamento, setIsDesarquivamento] = useState(false);

  const openModal = (id: string, desarquivar = false) => {
    setCumprimentoId(id);
    setIsDesarquivamento(desarquivar);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCumprimentoId('');
    setIsDesarquivamento(false);
  };

  return {
    isOpen,
    cumprimentoId,
    isDesarquivamento,
    openModal,
    closeModal,
  };
};

type MotivoArquivamentoHandler = (dados: DadosArquivamento) => void | Promise<void>;
